import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Download, FileArrowUp, FileText, CheckCircle, Warning, X, ClockClockwise
} from '@phosphor-icons/react';

const ENTITIES = [
  { key: 'authors', label: 'Authors', icon: '✍️' },
  { key: 'works', label: 'Works', icon: '📚' },
  { key: 'categories', label: 'Categories', icon: '🏷️' },
  { key: 'articles', label: 'Articles', icon: '📝' },
];

export default function CmsBulkImport() {
  const [selectedEntity, setSelectedEntity] = useState('authors');
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [updateExisting, setUpdateExisting] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-import/history`, {
        withCredentials: true,
      });
      setHistory(r.data.history || []);
    } catch (e) {
      console.error('Failed to load history');
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-import/templates/${selectedEntity}`,
        { withCredentials: true }
      );
      
      const blob = new Blob([r.data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = r.data.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setValidation(null);

    // Auto-validate
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('entity', selectedEntity);

    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-import/validate`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setValidation(r.data);
      
      if (r.data.can_import) {
        toast.success(`Validation passed: ${r.data.valid_count} rows ready`);
      } else {
        toast.warning(`Validation found ${r.data.error_count} errors`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    }
  };

  const executeImport = async () => {
    if (!file || !validation || !validation.can_import) {
      toast.error('Please upload and validate a file first');
      return;
    }

    if (!window.confirm(`Import ${validation.valid_count} ${selectedEntity}?`)) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity', selectedEntity);
    formData.append('update_existing', updateExisting);

    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-import/execute`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(r.data.message);
      setFile(null);
      setValidation(null);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-bulk-import">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <FileArrowUp size={28} weight="duotone" className="text-primary" />
          Bulk Import Center
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Import authors, works, articles, and categories from CSV or Excel files
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Import Area */}
        <div className="lg:col-span-2 space-y-4">
          {/* Entity Selection */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-sm uppercase tracking-widest mb-3">Step 1: Select Import Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ENTITIES.map((entity) => (
                <button
                  key={entity.key}
                  onClick={() => {
                    setSelectedEntity(entity.key);
                    setFile(null);
                    setValidation(null);
                  }}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    selectedEntity === entity.key
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`entity-${entity.key}`}
                >
                  <div className="text-2xl mb-1">{entity.icon}</div>
                  <div className="text-sm">{entity.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Template Download */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-sm uppercase tracking-widest mb-3">Step 2: Download Template</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Download the CSV template, fill it with your data, and upload it below
            </p>
            <button
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/70 rounded-lg text-sm font-semibold"
              data-testid="download-template"
            >
              <Download size={16} /> Download {ENTITIES.find(e => e.key === selectedEntity)?.label} Template
            </button>
          </div>

          {/* File Upload */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-sm uppercase tracking-widest mb-3">Step 3: Upload File</h3>
            
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary hover:bg-primary/5">
              <Upload size={40} weight="duotone" className="text-muted-foreground mb-2" />
              <p className="text-sm font-semibold mb-1">
                {file ? file.name : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-muted-foreground">CSV or Excel (.xlsx) files</p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-upload-input"
              />
            </label>

            {/* Validation Results */}
            {validation && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                  <div className="flex items-center gap-2">
                    {validation.can_import ? (
                      <CheckCircle size={20} weight="fill" className="text-green-600" />
                    ) : (
                      <Warning size={20} weight="fill" className="text-amber-600" />
                    )}
                    <div>
                      <p className="text-sm font-semibold">
                        {validation.can_import ? 'Validation Passed' : 'Validation Errors Found'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {validation.valid_count} valid, {validation.error_count} errors
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground">
                    <X size={16} />
                  </button>
                </div>

                {/* Errors */}
                {validation.errors && validation.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs font-semibold uppercase tracking-widest text-red-800 mb-2">
                      Errors ({validation.error_count})
                    </p>
                    <div className="space-y-1 text-xs">
                      {validation.errors.slice(0, 5).map((err, i) => (
                        <div key={i} className="text-red-700">
                          Row {err.row}: {err.errors.join(', ')}
                        </div>
                      ))}
                      {validation.errors.length > 5 && (
                        <p className="text-red-600 italic">...and {validation.errors.length - 5} more errors</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview */}
                {validation.preview && validation.preview.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">
                      Preview (First 5 Rows)
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-blue-200">
                            {Object.keys(validation.preview[0]).map((key) => (
                              <th key={key} className="text-left p-1 text-blue-900">{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {validation.preview.map((row, i) => (
                            <tr key={i} className="border-b border-blue-100">
                              {Object.values(row).map((val, j) => (
                                <td key={j} className="p-1 text-blue-800">{val || '—'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Import Options */}
            {validation && validation.can_import && (
              <div className="mt-4 space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    data-testid="update-existing-checkbox"
                  />
                  Update existing records (if found by name/title)
                </label>
                <button
                  onClick={executeImport}
                  disabled={importing}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
                  data-testid="execute-import-btn"
                >
                  {importing ? 'Importing...' : `Import ${validation.valid_count} ${selectedEntity}`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Import History */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-lg p-4 sticky top-4">
            <h3 className="font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
              <ClockClockwise size={16} /> Recent Imports
            </h3>
            {history.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No imports yet</p>
            ) : (
              <div className="space-y-2">
                {history.slice(0, 10).map((item, i) => (
                  <div key={i} className="bg-background rounded p-2 text-xs">
                    <p className="font-semibold truncate">{item.details}</p>
                    <p className="text-muted-foreground">{new Date(item.timestamp).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
